import { MkIcon } from "@/mk/MkIcon";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "@/api/client";
import { useToast } from "@/app/toast";
import { MkEmptyState } from "@/mk/MkEmptyState";
import { MkModal } from "@/mk/MkModal";
import { MkSpinner } from "@/mk/MkSpinner";

interface Choices {
    active_id: number;
    can_switch: boolean;
    companies: { id: number; name: string; logo_url: string }[];
}

export function PmCompanySwitcher({ onClose }: { onClose(): void }) {
    const { t } = useTranslation();
    const toast = useToast();
    const [busy, setBusy] = useState(0);
    const choices = useQuery<{ data: Choices }>({
        queryKey: ["me", "companies"],
        queryFn: () => api.raw<Choices>("/me/companies"),
        staleTime: 0,
    });

    if (choices.isError) {
        toast.danger(choices.error instanceof Error && choices.error.message
            ? choices.error.message : t("pm.switcher.readFailed"));
    }

    const data = choices.data?.data;
    const companies = data?.companies ?? [];

    async function pick(companyId: number) {
        if (busy) {
            return;
        }
        if (companyId === data?.active_id) {
            onClose();
            return;
        }
        setBusy(companyId);
        try {
            await api.put(`/me/companies/${companyId}`);
            window.location.reload();
        } catch (error) {
            setBusy(0);
            toast.danger(error instanceof Error && error.message
                ? error.message : t("pm.switcher.switchFailed"));
        }
    }

    return (
        <MkModal open size="sm" icon="fa-building-o" title={t("pm.switcher.title")}
                 subtitle={t("pm.switcher.subtitle")} onClose={onClose}>
            {choices.isLoading ? (
                <MkSpinner center label={t("pm.switcher.title")} />
            ) : !companies.length ? (
                <MkEmptyState icon="fa-building-o" title={t("pm.switcher.empty")} />
            ) : (
                <div className="pm-company-switch">
                    {companies.map((company) => (
                        <button key={company.id} type="button"
                                className={`pm-company-switch-item ${company.id === data?.active_id ? "is-active" : ""}`}
                                disabled={Boolean(busy)} onClick={() => void pick(company.id)}>
                            <img className="pm-company-switch-logo" src={company.logo_url} alt="" />
                            <span className="pm-company-switch-name">{company.name}</span>
                            {company.id === data?.active_id && (
                                <span className="pm-company-switch-tag">{t("pm.switcher.current")}</span>
                            )}
                            {busy === company.id && <MkIcon name="fa-spinner fa-spin" />}
                        </button>
                    ))}
                </div>
            )}
        </MkModal>
    );
}
